import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Divider,
  EmptyStation,
  type FormActionData,
  FormStation,
  InfoPanel,
  type InitialFormData,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  RadioField,
  ReadOnlyField,
  Section,
  SelectField,
  SingleLineTextField,
  type StationMessage,
  formatDateTime,
  getFormDiff,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import {
  IdentityProvider,
  type IdpConfiguration,
  useDeleteIdpConfigurationMutation,
  useIdpConfigurationsQuery,
  useUpdateIdpConfigurationMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import {
  type CustomBrandingConfig,
  CustomIdpBranding,
  customBrandingValidation,
} from '../IdentityProviders/CustomIdpBranding/CustomIdpBranding';

interface FormData extends CustomBrandingConfig {
  id?: string;
  discoveryDocumentUrl?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  clientSecretIsSet?: boolean;
  scopes?: (string | null)[];
  azureTenant?: string;
}

const getIdpConfigurationSchema =
  (): Yup.ObjectSchema<ObjectSchemaDefinition> => {
    return Yup.object<ObjectSchemaDefinition>({
      azureTenant: Yup.string()
        .max(255)
        .matches(
          /^(([a-z0-9]|[a-z0-9][a-z0-9-.]*[a-z0-9]))$/,
          'Invalid format. Azure Tenant must be a valid domain name or a GUID',
        )
        .when('enabled', {
          is: true,
          then: Yup.string().required(
            'Tenant ID is required when IDP is enabled.',
          ),
          otherwise: Yup.string().nullable(),
        }),
      discoveryDocumentUrl: Yup.string()
        .max(255)
        .test(
          'discovery-document-url',
          'Discovery Document URL must be a valid URL',
          validateUrl,
        )
        .nullable(),
      clientId: Yup.string()
        .max(255)
        .when('enabled', {
          is: true,
          then: Yup.string().required(
            'Client ID is required when IDP is enabled.',
          ),
          otherwise: Yup.string().nullable(),
        }),
      clientSecret: Yup.string()
        .when(['enabled', 'clientSecretIsSet'], {
          is: (enabled: boolean, clientSecretIsSet: boolean) =>
            enabled && !clientSecretIsSet,
          then: Yup.string().required(
            'Client Secret is required when IDP is enabled.',
          ),
          otherwise: Yup.string().nullable(),
        })
        .max(255),
    }).concat(customBrandingValidation);
  };

const placeholderBuiltInAzureAdIdpClientId = 'BUILT_IN_AZURE_AD_IDP_CLIENT_ID';
const placeholderBuiltInAzureAdIdpClientSecret =
  'BUILT_IN_AZURE_AD_IDP_CLIENT_SECRET';

export const IdpAzureAdDetail: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const apolloClient = useApolloClient();

  // Check the status of id-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.ID_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const { loading, data, error } = useIdpConfigurationsQuery({
    client: apolloClient,
    variables: {
      idpConfigurationCondition: {
        environmentId: envId,
        idpId: IdentityProvider.AzureAd,
      },
      idpScopeFilter: {
        idpId: {
          equalTo: IdentityProvider.AzureAd,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const [updateIdpConfig] = useUpdateIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const idpConfiguration = data?.configurations?.nodes[0];
  let detailObj: FormData;
  if (idpConfiguration !== undefined) {
    detailObj = idpConfiguration;

    if (
      detailObj.discoveryDocumentUrl !== undefined &&
      detailObj.discoveryDocumentUrl !== null
    ) {
      const url = new URL(detailObj.discoveryDocumentUrl);
      const azureTenant = url.pathname.split('/')[1] ?? '';

      detailObj.azureTenant = azureTenant;
    }
  } else {
    detailObj = {
      discoveryDocumentUrl: null,
      clientId: null,
      clientSecret: null,
    };
  }

  const { actions } = useActions(envId, idpConfiguration?.id);

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: InitialFormData<FormData>,
    ): Promise<void> => {
      if (idpConfiguration !== undefined) {
        await updateIdpConfig({
          variables: {
            input: {
              id: initialData.data?.id,
              patch: createUpdateDto(formData, initialData.data),
            },
          },
        });
      }
    },
    [idpConfiguration, updateIdpConfig],
  );

  const [message] = useState<StationMessage>({
    title:
      'Certain Identity Providers may have different policies for automatically expiring Client Secrets. Please ensure if/when such expiration happens, the values stored here will be updated in a timely manner to provide an uninterrupted authentication experience.',
    type: 'info',
  });

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation
          title="Microsoft Entra ID (Azure AD) Identity Provider"
          subtitle="Configuration"
        >
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <FormStation<FormData>
          defaultTitle="Microsoft Entra ID (Azure AD) Identity Provider"
          subtitle="Configuration"
          actions={actions}
          validationSchema={getIdpConfigurationSchema()}
          initialData={{
            data: detailObj,
            loading,
            error: error?.message,
          }}
          saveData={onSubmit}
          infoPanel={idpConfiguration ? <Panel /> : undefined}
          stationMessage={message}
        >
          <Form isNew={!idpConfiguration} />
        </FormStation>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<IdpConfiguration>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC<{
  isNew: boolean;
}> = ({ isNew }) => {
  const { values, setFieldValue } = useFormikContext<FormData>();
  const [authority, setAuthority] = useState<string>('custom');

  // Initialize authority state based on azureTenant on mount
  useEffect(() => {
    setAuthority(
      values.clientId === placeholderBuiltInAzureAdIdpClientId &&
        values.azureTenant &&
        ['organizations'].includes(values.azureTenant)
        ? values.azureTenant
        : 'custom',
    );
  }, [values.azureTenant, values.clientId]);

  useEffect(() => {
    if (values.azureTenant) {
      setFieldValue(
        'discoveryDocumentUrl',
        `https://login.microsoftonline.com/${values.azureTenant}/v2.0/.well-known/openid-configuration`,
        false,
      );
    } else {
      setFieldValue('discoveryDocumentUrl', null, false);
    }
  }, [setFieldValue, values.azureTenant]);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onAuthorityChange = async (e: { currentTarget: { value: string } }) => {
    setAuthority(e.currentTarget.value);

    const currentAuthority = e.currentTarget.value;

    if (currentAuthority === 'organizations') {
      setFieldValue('clientId', placeholderBuiltInAzureAdIdpClientId);
      setFieldValue('clientSecret', placeholderBuiltInAzureAdIdpClientSecret);
      setFieldValue('azureTenant', currentAuthority);
    } else {
      setFieldValue('clientId', '');
      setFieldValue('clientSecret', '');
      setFieldValue('clientSecretIsSet', false);
      setFieldValue('azureTenant', '');
    }
  };

  return (
    <>
      {!isNew && (
        <>
          <Field
            name="enabled"
            label="IDP Status"
            options={[
              { value: true, label: 'Enabled' },
              { value: false, label: 'Disabled' },
            ]}
            transform={(value: string) => JSON.parse(value)}
            as={RadioField}
          />
          <Divider />
        </>
      )}

      <Field
        name="authority"
        label="Authority"
        tooltipContent={
          <div>
            <dl>
              <dt>Standard</dt>
              <dd>
                All users with a work or school account from Microsoft Entra ID
                can use this IDP to authenticate.
              </dd>

              <dt>Custom</dt>
              <dd>
                You can create your own Entra ID application in your
                organization&apos;s Azure portal and configure according to your
                preferences. After which, you will need to specify the Client
                ID, Client Secret & the appropriate Azure Tenant value of that
                Entra ID application below. <br /> <br />
                Refer to{' '}
                <a href="https://docs.axinom.com/platform/core/identity/configure-identity-providers/#pre-requisites-1">
                  Mosaic Documentation
                </a>{' '}
                for more information.
              </dd>
            </dl>
          </div>
        }
        options={[
          { id: 'custom', name: 'Custom' },
          { id: 'organizations', name: 'Standard' },
        ]?.map((auth) => ({
          value: auth.id,
          label: auth.name,
        }))}
        as={SelectField}
        onChange={onAuthorityChange}
        value={authority}
      />

      {authority === 'custom' && (
        <>
          <Field
            name="clientId"
            label="Client ID"
            autoComplete="off"
            as={SingleLineTextField}
          />
          <Field
            name="clientSecret"
            label="Client Secret"
            type="password"
            autoComplete="one-time-code"
            isSet={values?.clientSecretIsSet}
            as={SingleLineTextField}
          />

          <Field
            name="azureTenant"
            label="Azure Tenant"
            as={SingleLineTextField}
          />
          <Field
            name="discoveryDocumentUrl"
            label="Discovery Document URL"
            autoComplete="off"
            as={ReadOnlyField}
          />
        </>
      )}
      <Divider />
      <CustomIdpBranding />
    </>
  );
};

const useActions = (
  envId: string,
  idpConfigId: string | undefined,
): {
  readonly actions: FormActionData[];
} => {
  const apolloClient = useApolloClient();
  const history = useHistory();
  const [deleteIdpConfig] = useDeleteIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const actions: FormActionData[] = [];
  if (idpConfigId !== undefined) {
    actions.push({
      label: 'Delete Configuration',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        await deleteIdpConfig({ variables: { input: { id: idpConfigId } } });
        history.push(
          `/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders`,
        );
      },
    });
  }

  return { actions } as const;
};

function createUpdateDto(
  currentValues: FormData,
  initialValues: FormData | null | undefined,
): Partial<FormData> {
  const updateData = getFormDiff(currentValues, initialValues);
  delete updateData['azureTenant'];
  delete updateData['clientSecretIsSet'];
  updateData.clientId = updateData.clientId === '' ? null : updateData.clientId;
  updateData.clientSecret =
    updateData.clientSecret === '' ? null : updateData.clientSecret;
  updateData.discoveryDocumentUrl =
    updateData.discoveryDocumentUrl === ''
      ? null
      : updateData.discoveryDocumentUrl;
  return updateData;
}

export const AzureAdProviderDetailCrumb: BreadcrumbResolver = () =>
  'Microsoft Entra ID (Azure AD)';
